import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Admin.css';
import { API_ENDPOINTS, getFetchConfig } from '../config/api';
import { AVAILABLE_IMAGES, getImage } from '../assets/images';
import ImageUploader from '../components/ImageUploader';
import ProductImageManager from '../components/ProductImageManager';
import { getQuotesForAdmin, updateQuoteStatus } from '../api';

const Admin = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [availableImages, setAvailableImages] = useState(AVAILABLE_IMAGES);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [quotesLoading, setQuotesLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [uploadMessage, setUploadMessage] = useState('');
  
  // Estado para o gerenciador de imagens
  const [imageManagerOpen, setImageManagerOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(null);

  // Estado para o slider da homepage
  const [sliderImages, setSliderImages] = useState([]);
  const [showAddSliderForm, setShowAddSliderForm] = useState(false);
  const [sliderFormData, setSliderFormData] = useState({
    image_url: '',
    title: '',
    description: '',
    display_order: 1
  });

  const [formData, setFormData] = useState({
    name: '',
    price: '',
    description: '',
    category: '',
    image: ''
  });

  // Verificar se é admin
  useEffect(() => {
    const token = localStorage.getItem('token');
    const username = localStorage.getItem('username');
    
    if (!token || username !== 'admin') {
      window.location.href = '/';
      return;
    }
    
    // Verificar se o token ainda é válido
    verifyToken();
    
    loadProducts();
    loadCategories();
    loadAvailableImages();
    if (activeTab === 'orders') {
      loadOrders();
    }
    if (activeTab === 'quotes') {
      loadQuotes();
    }
    if (activeTab === 'slider') {
      loadSliderImages();
    }
  }, [activeTab]);

  const verifyToken = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.PROFILE, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        console.error('Token inválido, redirecionando para login...');
        localStorage.removeItem('token');
        localStorage.removeItem('username');
        window.location.href = '/';
        return;
      }
      
      const data = await response.json();
      if (!data.user.isAdmin) {
        console.error('Usuário não é admin');
        window.location.href = '/';
        return;
      }
    } catch (error) {
      console.error('Erro ao verificar token:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('username');
      window.location.href = '/';
    }
  };

  const loadProducts = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.PRODUCTS, getFetchConfig('GET'));
      const data = await response.json();
      if (data.status === 'ok') {
        setProducts(data.products);
      }
    } catch (error) {
      console.error('Erro ao carregar produtos:', error);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.CATEGORIES, getFetchConfig('GET'));
      const data = await response.json();
      if (data.status === 'ok') {
        setCategories(data.categories);
      }
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    }
  };

  const loadAvailableImages = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.IMAGES, getFetchConfig('GET'));
      const data = await response.json();
      if (data.status === 'ok') {
        setAvailableImages(data.images);
      }
    } catch (error) {
      console.error('Erro ao carregar imagens:', error);
    }
  };

  const loadOrders = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.ADMIN_ORDERS, getFetchConfig('GET'));
      const data = await response.json();
      if (data.status === 'ok') {
        setOrders(data.orders);
      }
    } catch (error) {
      console.error('Erro ao carregar pedidos:', error);
    }
  };

  const loadQuotes = async () => {
    setQuotesLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await getQuotesForAdmin(token);
      if (response.status === 'ok') {
        setQuotes(response.quotes);
      } else {
        setMessage(response.error || 'Erro ao carregar orçamentos');
      }
    } catch (error) {
      setMessage('Erro ao carregar orçamentos: ' + error.message);
    } finally {
      setQuotesLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, status) => {
    try {
      const response = await fetch(`${API_ENDPOINTS.ADMIN_ORDERS}/${orderId}`, getFetchConfig('PUT', { status }));
      const data = await response.json();
      if (data.status === 'ok') {
        setMessage('Estado do pedido atualizado com sucesso!');
        loadOrders();
      } else {
        setMessage(data.error || 'Erro ao atualizar pedido');
      }
    } catch (error) {
      setMessage('Erro ao atualizar pedido: ' + error.message);
    }
  };

  const deleteOrder = async (orderId) => {
    if (!window.confirm('Tem certeza que deseja eliminar este pedido?')) {
      return;
    }

    try {
      const response = await fetch(`${API_ENDPOINTS.ADMIN_ORDERS}/${orderId}`, getFetchConfig('DELETE'));
      const data = await response.json();
      if (data.status === 'ok') {
        setMessage('Pedido eliminado com sucesso!');
        loadOrders();
      } else {
        setMessage(data.error || 'Erro ao eliminar pedido');
      }
    } catch (error) {
      setMessage('Erro ao eliminar pedido: ' + error.message);
    }
  };

  const handleUploadSuccess = (fileName, originalName) => {
    setUploadMessage(`✅ Imagem "${originalName}" enviada com sucesso!`);
    // Recarregar lista de imagens
    loadAvailableImages();
    // Limpar mensagem após 5 segundos
    setTimeout(() => setUploadMessage(''), 5000);
    
    // Automaticamente selecionar a imagem recém-enviada
    setFormData(prev => ({
      ...prev,
      image: fileName
    }));
  };

  const handleUploadError = (error) => {
    setUploadMessage(`❌ Erro: ${error}`);
    // Limpar mensagem após 5 segundos
    setTimeout(() => setUploadMessage(''), 5000);
  };

  const handleDeleteImage = async (imageName) => {
    if (!window.confirm(`Tem certeza que deseja deletar a imagem "${imageName}"? Esta ação não pode ser desfeita.`)) {
      return;
    }

    try {
      const config = getFetchConfig('DELETE');
      const response = await fetch(`${API_ENDPOINTS.IMAGES}/${encodeURIComponent(imageName)}`, config);
      const data = await response.json();

      if (data.status === 'ok') {
        setUploadMessage(`✅ ${data.message}`);
        // Recarregar lista de imagens
        loadAvailableImages();
        // Se a imagem deletada estava selecionada no form, limpar seleção
        if (formData.image === imageName) {
          setFormData(prev => ({
            ...prev,
            image: ''
          }));
        }
      } else {
        setUploadMessage(`❌ Erro: ${data.error}`);
      }
    } catch (error) {
      setUploadMessage(`❌ Erro ao deletar imagem: ${error.message}`);
    }
    
    // Limpar mensagem após 5 segundos
    setTimeout(() => setUploadMessage(''), 5000);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const url = editingProduct 
        ? `${API_ENDPOINTS.PRODUCTS}/${editingProduct.id}`
        : API_ENDPOINTS.PRODUCTS;
      
      const method = editingProduct ? 'PUT' : 'POST';

      const response = await fetch(url, getFetchConfig(method, formData));
      const data = await response.json();

      if (data.status === 'ok') {
        setMessage(editingProduct ? 'Produto atualizado com sucesso!' : 'Produto criado com sucesso!');
        setFormData({
          name: '',
          price: '',
          description: '',
          category: '',
          image: ''
        });
        setShowAddForm(false);
        setEditingProduct(null);
        loadProducts();
        loadCategories();
      } else {
        setMessage(data.error || 'Erro ao salvar produto');
      }
    } catch (error) {
      if (error.message === 'Token inválido') {
        console.error('Token inválido, redirecionando para login...');
        localStorage.removeItem('token');
        localStorage.removeItem('username');
        window.location.href = '/';
        return;
      }
      setMessage('Erro ao salvar produto: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      price: product.price.toString(),
      description: product.description,
      category: product.category,
      image: product.image
    });
    
    setShowAddForm(true);
  };

  const handleManageImages = (productId) => {
    setSelectedProductId(productId);
    setImageManagerOpen(true);
  };

  const handleImagesUpdate = () => {
    loadProducts(); // Recarregar produtos para atualizar as imagens
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Tem certeza que deseja eliminar este produto?')) {
      return;
    }

    try {
      const response = await fetch(`${API_ENDPOINTS.PRODUCTS}/${productId}`, getFetchConfig('DELETE'));
      const data = await response.json();

      if (data.status === 'ok') {
        setMessage('Produto eliminado com sucesso!');
        loadProducts();
      } else {
        setMessage(data.error || 'Erro ao eliminar produto');
      }
    } catch (error) {
      setMessage('Erro ao eliminar produto: ' + error.message);
    }
  };

  const cancelEdit = () => {
    setEditingProduct(null);
    setShowAddForm(false);
    setFormData({
      name: '',
      price: '',
      description: '',
      category: '',
      image: ''
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pendente': return '#ffc107';
      case 'processando': return '#17a2b8';
      case 'enviado': return '#007bff';
      case 'entregue': return '#28a745';
      case 'cancelado': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const handleUpdateQuoteStatus = async (quoteId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      const response = await updateQuoteStatus(token, quoteId, newStatus);
      if (response.status === 'ok') {
        setMessage('Status atualizado com sucesso');
        loadQuotes(); // Recarregar orçamentos
      } else {
        setMessage(response.error || 'Erro ao atualizar status');
      }
    } catch (error) {
      setMessage('Erro ao atualizar status: ' + error.message);
    }
  };

  // Funções do Slider
  const loadSliderImages = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.HOME_SLIDER, getFetchConfig('GET'));
      const data = await response.json();
      if (data.status === 'ok') {
        setSliderImages(data.images);
      }
    } catch (error) {
      console.error('Erro ao carregar imagens do slider:', error);
    }
  };

  const handleAddSliderImage = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch(API_ENDPOINTS.ADMIN_HOME_SLIDER, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(sliderFormData)
      });

      const data = await response.json();
      
      if (data.status === 'ok') {
        setMessage('Imagem do slider adicionada com sucesso!');
        setSliderFormData({
          image_url: '',
          title: '',
          description: '',
          display_order: 1
        });
        setShowAddSliderForm(false);
        loadSliderImages();
      } else {
        setMessage('Erro ao adicionar imagem do slider: ' + data.error);
      }
    } catch (error) {
      setMessage('Erro ao adicionar imagem do slider: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSliderImage = async (imageId) => {
    if (!window.confirm('Tem certeza que deseja deletar esta imagem do slider?')) {
      return;
    }

    try {
      const response = await fetch(`${API_ENDPOINTS.ADMIN_HOME_SLIDER}/${imageId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();
      
      if (data.status === 'ok') {
        setMessage('Imagem do slider deletada com sucesso!');
        loadSliderImages();
      } else {
        setMessage('Erro ao deletar imagem do slider: ' + data.error);
      }
    } catch (error) {
      setMessage('Erro ao deletar imagem do slider: ' + error.message);
    }
  };

  const handleUpdateSliderOrder = async (imageId, newOrder) => {
    try {
      const response = await fetch(`${API_ENDPOINTS.ADMIN_HOME_SLIDER}/${imageId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ display_order: newOrder })
      });

      const data = await response.json();
      
      if (data.status === 'ok') {
        setMessage('Ordem atualizada com sucesso!');
        loadSliderImages();
      } else {
        setMessage('Erro ao atualizar ordem: ' + data.error);
      }
    } catch (error) {
      setMessage('Erro ao atualizar ordem: ' + error.message);
    }
  };

  return (
    <div className="admin-container">
      <div className="admin-header">
        <div className="admin-header-top">
          <h1>Painel de Administração</h1>
          <div className="admin-header-actions">
            {activeTab === 'products' && (
              <button 
                className="btn btn-primary"
                onClick={() => setShowAddForm(true)}
              >
                Adicionar Produto
              </button>
            )}
            {activeTab === 'slider' && (
              <button 
                className="btn btn-primary"
                onClick={() => setShowAddSliderForm(true)}
              >
                Adicionar Imagem
              </button>
            )}
          </div>
        </div>
        <div className="admin-tabs">
          <button 
            className={`tab-btn ${activeTab === 'products' ? 'active' : ''}`}
            onClick={() => setActiveTab('products')}
          >
            Produtos
          </button>
          <button 
            className={`tab-btn ${activeTab === 'orders' ? 'active' : ''}`}
            onClick={() => setActiveTab('orders')}
          >
            Pedidos
          </button>
          <button 
            className={`tab-btn ${activeTab === 'quotes' ? 'active' : ''}`}
            onClick={() => setActiveTab('quotes')}
          >
            Orçamentos
          </button>
          <button 
            className={`tab-btn ${activeTab === 'images' ? 'active' : ''}`}
            onClick={() => setActiveTab('images')}
          >
            Imagens
          </button>
          <button 
            className={`tab-btn ${activeTab === 'slider' ? 'active' : ''}`}
            onClick={() => setActiveTab('slider')}
          >
            Slider Homepage
          </button>
        </div>
      </div>

      {message && (
        <div className={`message ${message.includes('sucesso') ? 'success' : 'error'}`}>
          {message}
        </div>
      )}

      {uploadMessage && (
        <div className={`message ${uploadMessage.startsWith('✅') ? 'success' : 'error'}`}>
          {uploadMessage}
        </div>
      )}

      {activeTab === 'products' && (
        <div className="products-section">
          {showAddForm && (
            <div className="modal-overlay">
              <div className="modal">
                <div className="modal-header">
                  <h2>{editingProduct ? 'Editar Produto' : 'Adicionar Produto'}</h2>
                  <button className="close-btn" onClick={cancelEdit}>×</button>
                </div>
                
                <form onSubmit={handleSubmit} className="product-form">
                  <div className="form-group">
                    <label htmlFor="productName">Nome do Produto:</label>
                    <input
                      type="text"
                      id="productName"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="productPrice">Preço (€):</label>
                    <input
                      type="number"
                      id="productPrice"
                      name="price"
                      value={formData.price}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="productDescription">Descrição:</label>
                    <textarea
                      id="productDescription"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="productCategory">Categoria:</label>
                    <input
                      type="text"
                      id="productCategory"
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      list="categories"
                    />
                    <datalist id="categories">
                      {categories.map(cat => (
                        <option key={cat} value={cat} />
                      ))}
                    </datalist>
                  </div>

                  <div className="form-group">
                    <label htmlFor="productImage">Imagem:</label>
                    
                    {/* Upload de nova imagem */}
                    <div className="upload-section">
                      <h4>Enviar Nova Imagem</h4>
                      <ImageUploader 
                        onUploadSuccess={handleUploadSuccess}
                        onError={handleUploadError}
                      />
                      {uploadMessage && (
                        <div className={`upload-message ${uploadMessage.includes('✅') ? 'success' : 'error'}`}>
                          {uploadMessage}
                        </div>
                      )}
                    </div>
                    
                    {/* Seletor de imagem existente */}
                    <div className="image-selector">
                      <h4>Ou Selecione uma Imagem Existente</h4>
                      <select
                        name="image"
                        id="productImage"
                        value={formData.image}
                        onChange={handleInputChange}
                        className="image-select"
                      >
                        <option value="">Selecione uma imagem</option>
                        {availableImages.map(imageName => (
                          <option key={imageName} value={imageName}>
                            {imageName}
                          </option>
                        ))}
                      </select>
                      {formData.image && (
                        <div className="image-preview">
                          {getImage(formData.image) ? (
                            <img 
                              src={getImage(formData.image)} 
                              alt="Pré-visualização" 
                              className="preview-image"
                            />
                          ) : (
                            <div className="preview-no-image">
                              <span>Imagem não encontrada</span>
                            </div>
                          )}
                          <span className="preview-name">{formData.image}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="form-actions">
                    <button type="button" onClick={cancelEdit}>Cancelar</button>
                    <button type="submit" disabled={loading}>
                      {(() => {
                        if (loading) return 'Guardando...';
                        return editingProduct ? 'Atualizar' : 'Adicionar';
                      })()}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          <div className="products-table">
            <h2>Produtos ({products.length})</h2>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Imagem</th>
                    <th>Nome</th>
                    <th>Preço</th>
                    <th>Categoria</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map(product => (
                    <tr key={product.id}>
                      <td>{product.id}</td>
                      <td>
                        <div className="product-image-cell">
                          <img 
                            src={getImage(product.image)} 
                            alt={product.name}
                            className="product-table-image"
                          />
                          <span className="image-name">{product.image}</span>
                        </div>
                      </td>
                      <td>{product.name}</td>
                      <td>€{(parseFloat(product.price) || 0).toFixed(2)}</td>
                      <td>{product.category}</td>
                      <td>
                        <div className="action-buttons">
                          <button 
                            className="btn btn-primary btn-images"
                            onClick={() => handleManageImages(product.id)}
                            title="Gerenciar Imagens"
                          >
                            📸 Imagens
                          </button>
                          <button 
                            className="btn btn-secondary"
                            onClick={() => handleEdit(product)}
                          >
                            Editar
                          </button>
                          <button 
                            className="btn btn-danger"
                            onClick={() => handleDelete(product.id)}
                          >
                            Eliminar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'orders' && (
        <div className="orders-section">
          <h2>Pedidos ({orders.length})</h2>
          <div className="orders-table">
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Cliente</th>
                  <th>Email</th>
                  <th>Items</th>
                  <th>Total</th>
                  <th>Estado</th>
                  <th>Data</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {orders.map(order => (
                  <tr key={order.id}>
                    <td>#{order.id}</td>
                    <td>{order.userId}</td>
                    <td>{order.userEmail}</td>
                    <td>
                      <div className="order-items-preview">
                        {order.items.map((item) => (
                          <div key={`${order.id}-${item.productId}`} className="item-preview">
                            {item.productName} (x{item.quantity})
                          </div>
                        ))}
                      </div>
                    </td>
                    <td>€{order.totalAmount.toFixed(2)}</td>
                    <td>
                      <select 
                        value={order.status} 
                        onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                        className="status-select"
                        style={{ backgroundColor: getStatusColor(order.status) }}
                      >
                        <option value="pendente">Pendente</option>
                        <option value="processando">Processando</option>
                        <option value="enviado">Enviado</option>
                        <option value="entregue">Entregue</option>
                        <option value="cancelado">Cancelado</option>
                      </select>
                    </td>
                    <td>
                      {new Date(order.createdAt).toLocaleDateString('pt-PT', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button 
                          className="btn btn-sm btn-primary"
                          onClick={() => navigate(`/admin/order/${order.id}`)}
                        >
                          Ver Detalhes
                        </button>
                        <button 
                          className="btn btn-sm btn-danger"
                          onClick={() => deleteOrder(order.id)}
                        >
                          Eliminar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'quotes' && (
        <div className="quotes-section">
          <h2>Orçamentos ({quotes.length})</h2>
          {quotesLoading ? (
            <div className="loading">Carregando orçamentos...</div>
          ) : (
            <div className="quotes-table">
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Cliente</th>
                    <th>Email</th>
                    <th>Telefone</th>
                    <th>Tipo</th>
                    <th>Descrição</th>
                    <th>Qtd/Items</th>
                    <th>Preço</th>
                    <th>Total</th>
                    <th>Status</th>
                    <th>Data</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {quotes.map(quote => {
                    // Extrair dados baseado no tipo de orçamento
                    const customerInfo = quote.customerInfo || {};
                    const isAdvanced = quote.type === 'advanced';
                    const isSimple = quote.type === 'simple';
                    
                    // Para orçamentos avançados, calcular produtos selecionados
                    let productInfo = 'N/A';
                    let totalQuantity = 0;
                    
                    if (isAdvanced && quote.products && Array.isArray(quote.products)) {
                      totalQuantity = quote.products.reduce((sum, product) => sum + product.quantity, 0);
                      productInfo = `${quote.products.length} produto(s)`;
                    } else if (isSimple) {
                      productInfo = 'Orçamento Simples';
                      totalQuantity = 'N/A';
                    }
                    
                    return (
                      <tr key={quote.id}>
                        <td>#{String(quote.id).slice(-6)}</td>
                        <td>{customerInfo.name || 'N/A'}</td>
                        <td>{customerInfo.email || 'N/A'}</td>
                        <td>{customerInfo.phone !== 'undefined' ? customerInfo.phone : 'N/A'}</td>
                        <td>
                          <span className={`quote-type ${quote.type}`}>
                            {quote.type === 'simple' ? 'Simples' : 'Avançado'}
                          </span>
                        </td>
                        <td>{productInfo}</td>
                        <td>{totalQuantity}</td>
                        <td>N/A</td>
                        <td>N/A</td>
                        <td>
                          <select 
                            value={quote.status} 
                            onChange={(e) => handleUpdateQuoteStatus(quote.id, e.target.value)}
                            className="status-select"
                            style={{ backgroundColor: getStatusColor(quote.status) }}
                          >
                            <option value="pendente">Pendente</option>
                            <option value="analisando">Analisando</option>
                            <option value="aprovado">Aprovado</option>
                            <option value="recusado">Recusado</option>
                          </select>
                        </td>
                        <td>
                          {new Date(quote.createdAt).toLocaleDateString('pt-PT', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button 
                              className="btn btn-sm btn-primary"
                              onClick={() => navigate(`/admin/quote/${quote.id}`)}
                            >
                              Ver Detalhes
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Seção de Gerenciamento de Imagens */}
      {activeTab === 'images' && (
        <div className="images-section">
          <h2>Gerenciar Imagens ({availableImages.length})</h2>
          
          {/* Upload de nova imagem */}
          <div className="upload-section">
            <h3>Enviar Nova Imagem</h3>
            <ImageUploader 
              onUploadSuccess={handleUploadSuccess} 
              onError={handleUploadError}
            />
            {uploadMessage && (
              <div className={`upload-message ${uploadMessage.startsWith('✅') ? 'success' : 'error'}`}>
                {uploadMessage}
              </div>
            )}
          </div>

          {/* Lista de imagens existentes */}
          <div className="images-list">
            <h3>Imagens Disponíveis</h3>
            {availableImages.length === 0 ? (
              <p>Nenhuma imagem disponível</p>
            ) : (
              <div className="images-grid">
                {availableImages.map(imageName => (
                  <div key={imageName} className="image-item">
                    <div className="image-preview-container">
                      {getImage(imageName) ? (
                        <img 
                          src={getImage(imageName)} 
                          alt={imageName} 
                          className="image-thumbnail"
                        />
                      ) : (
                        <div className="image-placeholder">
                          <span>Imagem não carregada</span>
                        </div>
                      )}
                    </div>
                    <div className="image-info">
                      <span className="image-file-name">{imageName}</span>
                      <div className="image-actions">
                        {/* Só permitir deletar imagens de uploads (não do sistema) */}
                        {!['camara.jpg', 'logo.png'].includes(imageName) && (
                          <button 
                            className="image-delete-btn"
                            onClick={() => handleDeleteImage(imageName)}
                            title="Deletar imagem"
                          >
                            🗑️ Deletar
                          </button>
                        )}
                        {['camara.jpg', 'logo.png'].includes(imageName) && (
                          <span className="system-image">Imagem do sistema</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Seção de Gerenciamento do Slider */}
      {activeTab === 'slider' && (
        <div className="slider-section">
          <h2>Gerenciar Slider da Homepage</h2>
          
          {/* Lista de imagens do slider */}
          <div className="slider-images-list">
            {sliderImages.length === 0 ? (
              <p>Nenhuma imagem no slider. Adicione uma imagem para começar.</p>
            ) : (
              <div className="slider-grid">
                {sliderImages.map((image) => (
                  <div key={image.id} className="slider-image-card">
                    <div className="slider-image-preview">
                      <img 
                        src={image.image_url.startsWith('http') ? image.image_url : `/uploads/${image.image_url}`}
                        alt={image.title || 'Imagem do slider'}
                        onError={(e) => {
                          e.target.src = '/uploads/default-image.jpg';
                        }}
                      />
                    </div>
                    <div className="slider-image-info">
                      <h4>{image.title || 'Sem título'}</h4>
                      <p>{image.description || 'Sem descrição'}</p>
                      <div className="slider-image-order">
                        <label>Ordem:</label>
                        <input
                          type="number"
                          min="1"
                          value={image.display_order}
                          onChange={(e) => handleUpdateSliderOrder(image.id, parseInt(e.target.value))}
                        />
                      </div>
                      <div className="slider-image-actions">
                        <button 
                          className="btn btn-danger btn-sm"
                          onClick={() => handleDeleteSliderImage(image.id)}
                        >
                          🗑️ Deletar
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Formulário de adicionar imagem */}
          {showAddSliderForm && (
            <div className="modal">
              <div className="modal-content">
                <div className="modal-header">
                  <h3>Adicionar Imagem ao Slider</h3>
                  <button 
                    className="close-btn"
                    onClick={() => setShowAddSliderForm(false)}
                  >
                    ×
                  </button>
                </div>
                <form onSubmit={handleAddSliderImage} className="slider-form">
                  <div className="form-group">
                    <label>Imagem:</label>
                    <select
                      value={sliderFormData.image_url}
                      onChange={(e) => setSliderFormData({...sliderFormData, image_url: e.target.value})}
                      required
                    >
                      <option value="">Selecione uma imagem</option>
                      {availableImages.map(imageName => (
                        <option key={imageName} value={imageName}>
                          {imageName}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label>Título:</label>
                    <input
                      type="text"
                      value={sliderFormData.title}
                      onChange={(e) => setSliderFormData({...sliderFormData, title: e.target.value})}
                      placeholder="Título da imagem (opcional)"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Descrição:</label>
                    <textarea
                      value={sliderFormData.description}
                      onChange={(e) => setSliderFormData({...sliderFormData, description: e.target.value})}
                      placeholder="Descrição da imagem (opcional)"
                      rows="3"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Ordem de Exibição:</label>
                    <input
                      type="number"
                      min="1"
                      value={sliderFormData.display_order}
                      onChange={(e) => setSliderFormData({...sliderFormData, display_order: parseInt(e.target.value)})}
                      required
                    />
                  </div>
                  
                  <div className="form-actions">
                    <button type="submit" className="btn btn-primary" disabled={loading}>
                      {loading ? 'Adicionando...' : 'Adicionar Imagem'}
                    </button>
                    <button 
                      type="button" 
                      className="btn btn-secondary"
                      onClick={() => setShowAddSliderForm(false)}
                    >
                      Cancelar
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Gerenciador de Imagens */}
      <ProductImageManager
        productId={selectedProductId}
        isOpen={imageManagerOpen}
        onClose={() => {
          setImageManagerOpen(false);
          setSelectedProductId(null);
        }}
        onImagesUpdate={handleImagesUpdate}
      />
    </div>
  );
};

export default Admin;
